import { effect } from "@preact/signals";
import Phaser from "phaser";
import { tid, WATER_TILE_IDS } from "@/assets/tileAtlas";
import { battle, getLastBattleResult, startBattle } from "@/battle/BattleController";
import { MONSTER_SPEED, PLAYER_SPEED, TILE } from "@/config";
import { NPCS } from "@/content/dialogs";
import { avatarDef } from "@/content/heroes";
import { getItem } from "@/content/items";
import { MONSTERS } from "@/content/monsters";
import { lt, t, type I18nKey } from "@/i18n";
import type { LocalizedText } from "@/math";
import { onChestOpened, onNpcTalked, questDialogFor } from "@/quests/QuestController";
import { saveGame, worldRuntime } from "@/state/save";
import {
  addItems,
  dialog,
  hero,
  lang,
  openChoice,
  openDialog,
  panel,
  screen,
  showToast,
  touch,
} from "@/state/store";
import { openTraining, training } from "@/training/TrainingController";
import { installKeyboard, isDown, KEYS } from "@/world/keyboard";
import { WorldLabels } from "@/world/labels";
import {
  buildWorldMap,
  TREE_SIZE,
  type GeneratedMap,
  type Grid,
  type GuardedChest,
  type SignSpawn,
} from "@/world/mapGen";

type Dir = "down" | "up" | "left" | "right";

interface Npc {
  id: string;
  sprite: Phaser.GameObjects.Sprite;
}

interface ChestView {
  def: GuardedChest;
  chest: Phaser.GameObjects.Sprite;
  guard: Phaser.GameObjects.Sprite | null;
}

const INTERACT_RANGE = 26;
/** How far a monster may stray from where it spawned before it turns back (bosses stay closer). */
const LEASH_PX = 7 * TILE;
const BOSS_LEASH_PX = 4 * TILE;
/** Sprites are depth-sorted by their feet; layers stay below this. */
const DEPTH_BASE = 100;

export class WorldScene extends Phaser.Scene {
  private map!: GeneratedMap;
  private player!: Phaser.Physics.Arcade.Sprite;
  private heroTexture = "hero-boy";
  private npcs: Npc[] = [];
  private monsters!: Phaser.Physics.Arcade.Group;
  private treeBodies!: Phaser.Physics.Arcade.StaticGroup;
  private chest!: Phaser.GameObjects.Sprite;
  private guardedChests: ChestView[] = [];
  private groundLayer!: Phaser.Tilemaps.TilemapLayer;
  private labels!: WorldLabels;
  private facing: Dir = "down";
  private actionWasDown = false;
  private invulnerableUntil = 0;
  private currentMonster: Phaser.Physics.Arcade.Sprite | null = null;
  private saveTimer = 0;
  private bridgeFixedApplied = false;
  private regionId = "";

  constructor() {
    super("World");
  }

  create(): void {
    this.map = buildWorldMap();
    const { groundLayer, objectLayer } = this.buildTilemap();
    this.groundLayer = groundLayer;
    this.labels = new WorldLabels();
    this.spawnTrees();
    this.spawnPlayer();
    this.spawnNpcs();
    this.spawnMonsters();
    this.spawnChests();
    this.applyBridgeState();

    this.physics.world.setBounds(0, 0, this.map.width * TILE, this.map.height * TILE);
    this.physics.add.collider(this.player, objectLayer);
    this.physics.add.collider(this.player, groundLayer);
    this.physics.add.collider(this.player, this.treeBodies);
    this.physics.add.collider(this.monsters, objectLayer);
    this.physics.add.collider(this.monsters, groundLayer);
    this.physics.add.collider(this.monsters, this.treeBodies);
    this.physics.add.collider(this.monsters, this.monsters);
    this.physics.add.overlap(this.player, this.monsters, (_p, m) =>
      this.onTouchMonster(m as Phaser.Physics.Arcade.Sprite),
    );

    const cam = this.cameras.main;
    cam.setBounds(0, 0, this.map.width * TILE, this.map.height * TILE);
    cam.startFollow(this.player, true, 0.12, 0.12);
    cam.setRoundPixels(true);

    installKeyboard();

    // Grace period on start so a monster wandering over the spawn point does not attack instantly.
    // Uses the game clock (keeps running while this scene is paused for a battle).
    this.invulnerableUntil = this.game.getTime() + 1500;

    this.events.on("battle-ended", this.onBattleEnded, this);
    // "Menu" button: the UI sets the screen back to the title; stop everything and show it
    const disposeExit = effect(() => {
      if (screen.value === "title" && this.scene.isActive("World")) {
        if (this.scene.isActive("Battle") || this.scene.isPaused("Battle")) this.scene.stop("Battle");
        this.scene.start("Title");
      }
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      disposeExit();
      this.events.off("battle-ended", this.onBattleEnded, this);
      this.labels.destroy();
      this.npcs = [];
      this.guardedChests = [];
      this.currentMonster = null;
      this.actionWasDown = false;
    });
  }

  private buildTilemap() {
    const map = this.make.tilemap({ data: this.map.base, tileWidth: TILE, tileHeight: TILE });
    const tiles = map.addTilesetImage("tiles", "tiles", TILE, TILE, 0, 0);
    if (!tiles) throw new Error("tiles texture missing");
    const baseLayer = map.createLayer(0, tiles, 0, 0);
    if (!baseLayer) throw new Error("could not create base layer");
    const fill = (name: string, data: Grid, depth: number) => {
      const layer = map.createBlankLayer(name, tiles);
      if (!layer) throw new Error(`could not create layer ${name}`);
      for (let y = 0; y < this.map.height; y++) {
        for (let x = 0; x < this.map.width; x++) {
          const idx = (data[y] as number[])[x] as number;
          if (idx >= 0) layer.putTileAt(idx, x, y);
        }
      }
      layer.setDepth(depth);
      return layer;
    };
    baseLayer.setDepth(0);
    const groundLayer = fill("ground", this.map.ground, 1);
    fill("decor", this.map.decor, 2);
    const objectLayer = fill("objects", this.map.objects, 3);
    groundLayer.setCollision(WATER_TILE_IDS);
    objectLayer.setCollisionByExclusion([-1]);
    return { groundLayer, objectLayer };
  }

  /** Trees are images sorted by their base line, with a small static body at the trunk. */
  private spawnTrees(): void {
    this.treeBodies = this.physics.add.staticGroup();
    for (const tree of this.map.trees) {
      const def = TREE_SIZE[tree.kind];
      this.add
        .image(tree.x, tree.y, def.texture, `tree_${tree.kind}`)
        .setOrigin(0.5, 1)
        .setDepth(DEPTH_BASE + tree.y);
      // rocks collide with their whole footprint; trees only with the trunk
      const zone = def.solid
        ? this.add.zone(tree.x, tree.y - def.h / 2 + 2, def.w - 2, def.h - 6)
        : this.add.zone(tree.x, tree.y - 5, Math.round(def.w * 0.6), 10);
      this.physics.add.existing(zone, true);
      this.treeBodies.add(zone);
    }
  }

  /** Broken bridge = water in the middle column (collides); repaired = planks. */
  private applyBridgeState(): void {
    const fixed = hero.value.flags["bridge_fixed"] === true;
    for (const cell of this.map.bridge) {
      const tile = this.groundLayer.putTileAt(fixed ? cell.fixedTile : tid("water"), cell.x, cell.y);
      tile.setCollision(!fixed);
    }
    this.bridgeFixedApplied = fixed;
  }

  private spawnPlayer(): void {
    const saved = hero.value.pos;
    const spawn = saved ?? this.map.spawns.player;
    this.heroTexture = avatarDef(hero.value.avatar).texture;
    this.player = this.physics.add.sprite(spawn.x, spawn.y, this.heroTexture, 0);
    this.player.setCollideWorldBounds(true);
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    body.setSize(10, 8).setOffset(3, 8);
  }

  private spawnNpcs(): void {
    for (const s of this.map.spawns.npcs) {
      const def = NPCS[s.npcId];
      if (!def) continue;
      const sprite = this.add.sprite(s.x, s.y, def.texture, 0).setDepth(DEPTH_BASE + s.y + 8);
      this.npcs.push({ id: s.npcId, sprite });
      this.labels.set(`npc-${s.npcId}`, def.name[lang.value]);
    }
  }

  private spawnMonsters(): void {
    this.monsters = this.physics.add.group();
    for (const s of this.map.spawns.monsters) {
      const def = MONSTERS[s.monsterId];
      if (!def) continue;
      const sprite = this.monsters.create(s.x, s.y, def.texture, 0) as Phaser.Physics.Arcade.Sprite;
      sprite.play(`${def.texture}-idle`);
      if (def.rank === "boss") sprite.setScale(1.5);
      sprite.setCollideWorldBounds(true);
      sprite.setData("monsterId", s.monsterId);
      sprite.setData("nextTurn", 0);
      sprite.setData("home", { x: s.x, y: s.y, leash: def.rank === "boss" ? BOSS_LEASH_PX : LEASH_PX });
      const body = sprite.body as Phaser.Physics.Arcade.Body;
      body.setSize(12, 10).setOffset(2, 6);
    }
  }

  /** The free chest in the garden plus the guarded treasure chests with their monster. */
  private spawnChests(): void {
    const c = this.map.spawns.chest;
    const opened = hero.value.flags["chest_village_1"] === true;
    this.chest = this.add.sprite(c.x, c.y, "chest", opened ? 1 : 0).setDepth(DEPTH_BASE + c.y);

    for (const def of this.map.spawns.guardedChests) {
      const isOpen = hero.value.flags[`chest_${def.id}`] === true;
      const chest = this.add.sprite(def.x, def.y, "bigchest", isOpen ? 1 : 0).setDepth(DEPTH_BASE + def.y);
      let guard: Phaser.GameObjects.Sprite | null = null;
      if (!isOpen) {
        const mdef = MONSTERS[def.guard];
        if (mdef) {
          guard = this.add.sprite(def.x + 18, def.y + 2, mdef.texture, 0).setDepth(DEPTH_BASE + def.y + 10);
          guard.play(`${mdef.texture}-idle`);
        }
      }
      this.guardedChests.push({ def, chest, guard });
    }
  }

  override update(time: number, delta: number): void {
    const actionDown = touch.action || isDown(...KEYS.action);
    const actionJustPressed = actionDown && !this.actionWasDown;
    this.actionWasDown = actionDown;

    this.sortSprites();
    const overlay = !!(dialog.value || battle.value || training.value || panel.value);
    this.updateLabels(overlay);

    if (overlay) {
      // While an overlay is open the action key must be released before it can interact again,
      // otherwise the press that closes a dialog would immediately re-open it.
      this.actionWasDown = true;
      this.player.setVelocity(0, 0);
      this.standStill();
      return;
    }

    if (!this.bridgeFixedApplied && hero.value.flags["bridge_fixed"]) {
      this.applyBridgeState();
      showToast(t("world.bridgeRepaired"), 3500);
    }

    this.movePlayer();
    this.wanderMonsters(time);
    this.updateInteractionHint();
    if (actionJustPressed) this.interact();

    this.saveTimer += delta;
    if (this.saveTimer > 1000) {
      this.saveTimer = 0;
      worldRuntime.pos = { x: Math.round(this.player.x), y: Math.round(this.player.y) };
      this.announceRegion();
    }
  }

  /** Show the region name when the hero walks into a new part of the world. */
  private announceRegion(): void {
    const tx = Math.floor(this.player.x / TILE);
    const ty = Math.floor(this.player.y / TILE);
    const region = this.map.regions.find((r) => tx >= r.x0 && tx <= r.x1 && ty >= r.y0 && ty <= r.y1);
    if (!region || region.id === this.regionId) return;
    const first = this.regionId === "";
    this.regionId = region.id;
    if (!first) showToast(`📍 ${lt(region.name)}`, 3000);
  }

  /** Depth = feet position, so the hero walks behind tree canopies and in front of trunks. */
  private sortSprites(): void {
    this.player.setDepth(DEPTH_BASE + this.player.y + 8.5);
    for (const obj of this.monsters.getChildren()) {
      const m = obj as Phaser.Physics.Arcade.Sprite;
      m.setDepth(DEPTH_BASE + m.y + 8);
    }
  }

  private updateLabels(hideAll: boolean): void {
    const view = this.cameras.main.worldView;
    for (const npc of this.npcs) {
      const id = `npc-${npc.id}`;
      const inView =
        npc.sprite.x > view.x - 40 &&
        npc.sprite.x < view.right + 40 &&
        npc.sprite.y > view.y - 40 &&
        npc.sprite.y < view.bottom + 40;
      if (hideAll || !inView) {
        this.labels.hide(id);
        continue;
      }
      this.labels.set(id, NPCS[npc.id]?.name[lang.value] ?? "");
      this.labels.place(id, this, npc.sprite.x, npc.sprite.y - 10);
    }
    if (hideAll) this.labels.hide("hint");
  }

  private movePlayer(): void {
    const left = touch.left || isDown(...KEYS.left);
    const right = touch.right || isDown(...KEYS.right);
    const up = touch.up || isDown(...KEYS.up);
    const down = touch.down || isDown(...KEYS.down);

    let vx = 0;
    let vy = 0;
    if (left) vx -= 1;
    if (right) vx += 1;
    if (up) vy -= 1;
    if (down) vy += 1;

    if (vx !== 0 && vy !== 0) {
      vx *= Math.SQRT1_2;
      vy *= Math.SQRT1_2;
    }
    this.player.setVelocity(vx * PLAYER_SPEED, vy * PLAYER_SPEED);

    if (vx !== 0 || vy !== 0) {
      if (Math.abs(vx) >= Math.abs(vy)) this.facing = vx < 0 ? "left" : "right";
      else this.facing = vy < 0 ? "up" : "down";
      this.player.play(`${this.heroTexture}-walk-${this.facing}`, true);
    } else {
      this.standStill();
    }
  }

  /** Stop the walk animation on the first frame of the row that faces the current direction. */
  private standStill(): void {
    const row = { down: 0, up: 1, left: 2, right: 3 }[this.facing];
    this.player.anims.stop();
    this.player.setFrame(row * 4);
  }

  private wanderMonsters(time: number): void {
    for (const obj of this.monsters.getChildren()) {
      const m = obj as Phaser.Physics.Arcade.Sprite;
      if (time < (m.getData("nextTurn") as number)) continue;
      m.setData("nextTurn", time + Phaser.Math.Between(800, 2200));
      const home = m.getData("home") as { x: number; y: number; leash: number } | undefined;
      const strayed = home ? Phaser.Math.Distance.Between(m.x, m.y, home.x, home.y) > home.leash : false;
      const roll = strayed ? 1 : Phaser.Math.Between(0, 4);
      if (roll === 0) {
        m.setVelocity(0, 0);
      } else {
        // beyond the leash: head roughly home, otherwise pick any direction
        const angle = strayed
          ? Math.atan2(home!.y - m.y, home!.x - m.x) + Phaser.Math.FloatBetween(-0.6, 0.6)
          : Phaser.Math.FloatBetween(0, Math.PI * 2);
        m.setVelocity(Math.cos(angle) * MONSTER_SPEED, Math.sin(angle) * MONSTER_SPEED);
        m.setFlipX(Math.cos(angle) < 0);
      }
    }
  }

  private nearestNpc(): Npc | null {
    let best: Npc | null = null;
    let bestDist = INTERACT_RANGE;
    for (const npc of this.npcs) {
      const d = Phaser.Math.Distance.Between(this.player.x, this.player.y, npc.sprite.x, npc.sprite.y);
      if (d < bestDist) {
        best = npc;
        bestDist = d;
      }
    }
    return best;
  }

  private nearChest(): boolean {
    return Phaser.Math.Distance.Between(this.player.x, this.player.y, this.chest.x, this.chest.y) < 22;
  }

  private nearGuardedChest(): ChestView | null {
    for (const c of this.guardedChests) {
      if (Phaser.Math.Distance.Between(this.player.x, this.player.y, c.chest.x, c.chest.y) < 26) return c;
    }
    return null;
  }

  private nearSign(): SignSpawn | null {
    for (const s of this.map.spawns.signs) {
      if (Phaser.Math.Distance.Between(this.player.x, this.player.y, s.x, s.y) < 26) return s;
    }
    return null;
  }

  private updateInteractionHint(): void {
    const npc = this.nearestNpc();
    const chest = this.nearChest() && hero.value.flags["chest_village_1"] !== true;
    const guarded = this.nearGuardedChest();
    const sign = this.nearSign();
    if (npc || chest || sign || guarded) {
      this.labels.set("hint", t("world.interact"), "hint");
      this.labels.place("hint", this, this.player.x, this.player.y - 12);
    } else {
      this.labels.hide("hint");
    }
  }

  private interact(): void {
    const npc = this.nearestNpc();
    if (npc) {
      this.talkTo(npc.id);
      return;
    }
    const guarded = this.nearGuardedChest();
    if (guarded) {
      this.approachGuardedChest(guarded);
      return;
    }
    const sign = this.nearSign();
    if (sign) {
      const key: I18nKey =
        sign.textKey === "sign.bridge"
          ? hero.value.flags["bridge_fixed"]
            ? "sign.bridge.fixed"
            : "sign.bridge.broken"
          : sign.textKey;
      const text = { pl: t(key, {}, "pl"), en: t(key, {}, "en") };
      openDialog({ pl: t("sign.title", {}, "pl"), en: t("sign.title", {}, "en") }, [text]);
      return;
    }
    if (this.nearChest()) {
      if (hero.value.flags["chest_village_1"] === true) {
        showToast(t("world.chestEmpty"));
        return;
      }
      const h = hero.value;
      hero.value = {
        ...h,
        gold: h.gold + 20,
        inventory: { ...h.inventory, potion: (h.inventory["potion"] ?? 0) + 1 },
        flags: { ...h.flags, chest_village_1: true },
      };
      this.chest.setFrame(1);
      showToast(t("world.chest", { gold: 20 }));
      saveGame();
    }
  }

  /** Ask before fighting the guard; refusing costs nothing. */
  private approachGuardedChest(c: ChestView): void {
    if (hero.value.flags[`chest_${c.def.id}`] === true) {
      showToast(t("chest.empty"));
      return;
    }
    const guard = MONSTERS[c.def.guard];
    if (!guard) return;
    const text = {
      pl: t("chest.guarded", { guard: guard.name.pl }, "pl"),
      en: t("chest.guarded", { guard: guard.name.en }, "en"),
    };
    openChoice(
      { pl: t("chest.title", {}, "pl"), en: t("chest.title", {}, "en") },
      text,
      [
        {
          label: { pl: t("chest.fight", {}, "pl"), en: t("chest.fight", {}, "en") },
          onSelect: () => {
            this.currentMonster = null;
            this.player.setVelocity(0, 0);
            startBattle(c.def.guard, { chestId: c.def.id });
            this.scene.launch("Battle");
            this.scene.pause();
          },
        },
        {
          label: { pl: t("chest.leave", {}, "pl"), en: t("chest.leave", {}, "en") },
          onSelect: () => undefined,
        },
      ],
      guard.faceset,
    );
  }

  private openGuardedChest(chestId: string): void {
    const view = this.guardedChests.find((c) => c.def.id === chestId);
    if (!view) return;
    const item = getItem(view.def.itemId);
    hero.value = addItems(
      { ...hero.value, flags: { ...hero.value.flags, [`chest_${chestId}`]: true } },
      { [view.def.itemId]: view.def.count },
    );
    view.chest.setFrame(1);
    if (view.guard) {
      const g = view.guard;
      this.tweens.add({ targets: g, alpha: 0, scale: 0.2, duration: 350, onComplete: () => g.destroy() });
      view.guard = null;
    }
    const label = view.def.count > 1 ? `${lt(item.name)} ×${view.def.count}` : lt(item.name);
    showToast(t("chest.opened", { item: `${item.icon} ${label}` }), 4000);
    onChestOpened(chestId);
  }

  /** Quest talk first (turn-in, progress, offer); the NPC's usual service (shop, training) opens afterwards. */
  private talkTo(npcId: string): void {
    const def = NPCS[npcId];
    if (!def) return;
    const name = hero.value.name;
    const personalise = (lines: LocalizedText[]) =>
      lines.map((l) => ({ pl: l.pl.replace("{name}", name), en: l.en.replace("{name}", name) }));
    const after =
      npcId === "quill" ? openTraining : npcId === "milo" ? () => (panel.value = "shop") : undefined;
    onNpcTalked(npcId); // delivery quests: reaching the NPC is the objective
    const quest = questDialogFor(npcId);
    if (quest) {
      openDialog(
        def.name,
        personalise(quest.lines),
        () => {
          quest.onClose?.();
          saveGame();
          after?.();
        },
        def.faceset,
      );
      return;
    }
    openDialog(def.name, personalise(def.lines), after, def.faceset);
  }

  private onTouchMonster(m: Phaser.Physics.Arcade.Sprite): void {
    if (battle.value || dialog.value || training.value || panel.value) return;
    if (this.game.getTime() < this.invulnerableUntil) return;
    this.currentMonster = m;
    this.player.setVelocity(0, 0);
    m.setVelocity(0, 0);
    startBattle(m.getData("monsterId") as string);
    this.scene.launch("Battle");
    this.scene.pause();
  }

  private onBattleEnded(): void {
    const result = getLastBattleResult();
    this.invulnerableUntil = this.game.getTime() + 1500;
    if (result?.won) {
      if (this.currentMonster) {
        const m = this.currentMonster;
        m.disableBody(false, false); // no more collisions while it fades out
        this.tweens.add({ targets: m, alpha: 0, scale: 0.2, duration: 350, onComplete: () => m.destroy() });
      }
      if (result.chestId) this.openGuardedChest(result.chestId);
    } else if (result?.fled) {
      // step away from the monster and give a longer grace period so it cannot re-engage instantly
      this.invulnerableUntil = this.game.getTime() + 2500;
      if (this.currentMonster) {
        const m = this.currentMonster;
        const dx = this.player.x - m.x;
        const dy = this.player.y - m.y;
        const len = Math.hypot(dx, dy) || 1;
        this.player.setPosition(this.player.x + (dx / len) * 20, this.player.y + (dy / len) * 20);
        m.setVelocity((-dx / len) * MONSTER_SPEED, (-dy / len) * MONSTER_SPEED);
        m.setData("nextTurn", this.time.now + 1500);
      }
    } else {
      const s = this.map.spawns.player;
      this.player.setPosition(s.x, s.y);
      this.cameras.main.centerOn(s.x, s.y);
    }
    this.currentMonster = null;
    worldRuntime.pos = { x: Math.round(this.player.x), y: Math.round(this.player.y) };
    saveGame();
  }
}
